import React, { useEffect, useState } from 'react'
import { Avatar } from '@astryxdesign/core/Avatar'
import PillButton from './PillButton'
import { Heading } from '@astryxdesign/core/Heading'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Timestamp } from '@astryxdesign/core/Timestamp'
import AuthModal from './AuthModal'
import StarRating from './StarRating'

const PAGE_SIZE = 10
const MAX_REVIEW_LENGTH = 4000

interface StoryReviewsProps {
  publicationId: string
  isOwnStory: boolean
  isSignedIn: boolean
  viewerRating: ViewerRating | null
  ratingAverage: number | null
  ratingCount: number
  onAggregateChange: (next: { ratingAverage: number | null; ratingCount: number }) => void
}

/**
 * The "Ratings & reviews" section under a published manuscript: the viewer's own rating form
 * first, then everyone else's reviews.
 *
 * Stars are the rating and the review text is optional, so submitting text with no stars is
 * refused client-side -- there is nothing to record. Star-only ratings are accepted and counted,
 * they simply never appear in the list below.
 */
export default function StoryReviews({
  publicationId,
  isOwnStory,
  isSignedIn,
  viewerRating,
  ratingAverage,
  ratingCount,
  onAggregateChange
}: StoryReviewsProps): React.JSX.Element {
  const [stars, setStars] = useState(viewerRating?.stars ?? 0)
  const [review, setReview] = useState(viewerRating?.review ?? '')
  const [hasRating, setHasRating] = useState(Boolean(viewerRating))
  const [reviews, setReviews] = useState<StoryReview[]>([])
  const [total, setTotal] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState('')
  const [showAuth, setShowAuth] = useState(false)

  const loadPage = (offset: number): void => {
    void window.api.storySocial
      .reviews(publicationId, { limit: PAGE_SIZE, offset })
      .then((page) => {
        setReviews((current) => (offset === 0 ? page.reviews : [...current, ...page.reviews]))
        setTotal(page.total)
      })
      .catch(() => undefined)
  }

  useEffect(() => {
    loadPage(0)
    // Reloading the first page whenever the aggregate moves keeps the list honest after the
    // viewer's own rating is added, edited or removed.
  }, [publicationId, ratingCount])

  const submit = async (): Promise<void> => {
    if (!isSignedIn) {
      setShowAuth(true)
      return
    }
    // Stars are the rating; the review is the optional half. Text with no stars is not a rating.
    if (stars < 1) {
      setError('Choose a star rating first.')
      return
    }
    setIsBusy(true)
    setError('')
    try {
      const result = await window.api.storySocial.rate(publicationId, {
        stars,
        review: review.trim()
      })
      setHasRating(true)
      onAggregateChange({ ratingAverage: result.ratingAverage, ratingCount: result.ratingCount })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save your rating.')
    } finally {
      setIsBusy(false)
    }
  }

  const remove = async (): Promise<void> => {
    setIsBusy(true)
    setError('')
    try {
      const result = await window.api.storySocial.removeRating(publicationId)
      setHasRating(false)
      setStars(0)
      setReview('')
      onAggregateChange({ ratingAverage: result.ratingAverage, ratingCount: result.ratingCount })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not remove your rating.')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <section className="story-reviews" id="story-reviews">
      <VStack gap={5}>
        <HStack gap={3} align="center" wrap="wrap">
          <Heading level={2}>Ratings &amp; reviews</Heading>
          {ratingCount > 0 ? (
            <HStack gap={2} align="center">
              <StarRating value={Math.round(ratingAverage ?? 0)} size={16} />
              <Text type="supporting">
                {ratingAverage} · {ratingCount} rating{ratingCount === 1 ? '' : 's'}
              </Text>
            </HStack>
          ) : (
            <Text type="supporting">No ratings yet.</Text>
          )}
        </HStack>

        {isOwnStory ? (
          <Text type="supporting">You wrote this, so you cannot rate it.</Text>
        ) : (
          <VStack gap={3} className="story-reviews-form">
            <StarRating
              value={stars}
              onChange={setStars}
              label={hasRating ? 'Your rating' : 'Rate this story'}
            />
            <TextArea
              label="Your review"
              value={review}
              onChange={setReview}
              placeholder="Optional — what did you make of it?"
              isOptional
              maxLength={MAX_REVIEW_LENGTH}
            />
            {error && (
              <Text type="supporting" className="story-reviews-error">
                {error}
              </Text>
            )}
            <HStack gap={2}>
              <PillButton
                label={hasRating ? 'Update rating' : 'Post rating'}
                isLoading={isBusy}
                onClick={() => void submit()}
              />
              {hasRating && (
                <PillButton
                  variant="ghost"
                  label="Remove rating"
                  isDisabled={isBusy}
                  onClick={() => void remove()}
                />
              )}
            </HStack>
          </VStack>
        )}

        {reviews.length > 0 && (
          <VStack gap={4}>
            {reviews.map((item) => (
              <article key={item.id} className="story-review">
                <HStack gap={3} align="start">
                  <Avatar size='md' name={item.penName} src={item.avatarUrl ?? undefined} />
                  <VStack gap={1}>
                    <HStack gap={2} align="center" wrap="wrap">
                      <a href={`/profiles/${encodeURIComponent(item.profileId)}`}>{item.penName}</a>
                      <StarRating value={item.stars} size={14} />
                      <Timestamp value={Math.floor(item.updatedAt / 1000)} format="date" />
                    </HStack>
                    <Text>{item.review}</Text>
                  </VStack>
                </HStack>
              </article>
            ))}
            {reviews.length < total && (
              <PillButton
                variant="secondary"
                label="Show more reviews"
                onClick={() => loadPage(reviews.length)}
              />
            )}
          </VStack>
        )}
      </VStack>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialMode="signIn" />
    </section>
  );
}
